<script>
    import Svg from "vpap/svelte/Svg.svelte";
    import {tooltipStore} from "vpap/svelte/stores";
    import Tooltip from "vpap/svelte/Tooltip.svelte";
    
    export let start_time, end_time, results;

    const tooltip = tooltipStore();


    let containerWidth;
    let screenWidth;
    const chartHeight = 450;

    const chartOffset = 30;
    const yPadding = 30;


    const dtParse = d3.timeParse("%Y-%m-%d %H:%M:%S");
    // const invertParse = d3.timeFormat("%Y-%m-%d %H:%M:%S");
    $: dtFormat = (containerWidth || 500) >= 600 ? d3.timeFormat("%I:%M %p") : d3.timeFormat("%I:%M");
    // const timeInterval = d3.timeMinute.every(30);

    let lineMouse = {
        x: undefined,
        hovered: false
    };

        $: minDate = dtParse(start_time);
        $: maxDate = dtParse(end_time);
        $: xScale = d3.scaleTime()
            .domain([minDate, maxDate])
            .range([0, containerWidth - (chartOffset * 2) - 30])
            .nice();
        $: xTicks = containerWidth > 450 ? xScale.ticks(6) : xScale.ticks(3);
        $: maxVoteShare = results.length > 0 ? Math.max(
            ...results.map(d => Math.max(...d.times.map(x => parseFloat(x.vote_share) * 100)))
        ) : 100;
        $: minVoteShare = results.length > 0 ? Math.min(
            ...results.map(d => Math.min(...d.times.map(x => parseFloat(x.vote_share) * 100)))
        ) : 0;

        $: yScale = d3.scaleLinear()
            .domain([minVoteShare, maxVoteShare])
            .range([chartHeight - yPadding, 15])
            .nice();
        $: yTicks = yScale.ticks(5);
        $: lineGen = d3.line()
            .x(d => xScale(dtParse(d.time)))
            .y(d => yScale(parseFloat(d.vote_share) * 100));
        // $: specTimes = timeInterval.range(minDate, maxDate);

    function handleMouseover() {
        lineMouse.hovered = true;
        tooltip.show();
    }

    function handleKeydown(e) {
        tooltip.keydown(e);
        const bbox = e.target.getBoundingClientRect();
        const changeAmount = 2;
        switch(e.key) {
            case "ArrowRight":
                {
                    const xOffset = Math.min((lineMouse.x || 0) + changeAmount, bbox.width);
                    mousePosition(xOffset);
                }
                break;
            case "ArrowLeft":
                {
                    const xOffset = Math.max((lineMouse.x || 0) - changeAmount, 0);
                    mousePosition(xOffset);
                }
                break;
        }
        tooltip.changePosition(bbox.left + lineMouse.x, bbox.top + 20);
    }

    function mousePosition(xOffset) {
        lineMouse.x = xOffset;
        const selDate = xScale.invert(xOffset);
        // converting to string makes equality comparison easier/work

        // const nearestTime = timeInterval.floor(selDate);
        let nearestTime, timeIndex;
        for (var i=0;i<results[0].times.length;i++) {
            const parsedTime = dtParse(results[0].times[i].time);
            if (parsedTime > selDate) break;
            nearestTime = parsedTime;
            timeIndex = i;
        }
        // const timeIndexRaw = specTimes.findIndex(d => invertParse(d) == invertParse(nearestTime));
        // extract the index for each time item based on the nearest found time to the mouse position
        // const timeIndex = timeIndexRaw > 0 ? timeIndexRaw : 
        //     specTimes[0] >= nearestTime ? 0 : 
        //         specTimes.length - 1;
        const timeData = {
            time: dtFormat(nearestTime),
            timeResults: results.map(d => {
                return {
                    last_name: d.last_name,
                    first_name: d.first_name,
                    color: d.color,
                    vote_share: parseFloat(d.times[timeIndex].vote_share) * 100
                };
            })
        };
        tooltip.updateData(timeData);
    }

    function handleMousemove(e) {
        const bbox = e.target.getBoundingClientRect();
        const xOffset = e.clientX - bbox.left;
        mousePosition(xOffset);
        tooltip.mousemove(e);
    }

    function handleMouseleave() {
        lineMouse.hovered = false;
        tooltip.hide();
    }

    function sortTooltip(a,b) {
        const diffVoteShare = parseFloat(b.vote_share) - parseFloat(a.vote_share);
        if (diffVoteShare !== 0) return diffVoteShare;
        else if (a.last_name < b.last_name) return -1;
        else if (a.last_name > b.last_name) return 1;
        else if (a.first_name < b.first_name) return -1;
        else if (a.first_name > b.first_name) return 1;
        else return 0;
    }
</script>

<svelte:window bind:innerWidth={screenWidth}/>
<template>
    <div class="sr-only sr-only-focusable" tabindex="0">Press the <kbd>Tab</kbd> button
        to see details about how candidates fared at a particular time. Use the arrow keys
        to move times, <kbd>Esc.</kbd> to hide the modal box, and <kbd>Space</kbd> to make it display.
    </div>
    <div class="timeline-container" bind:clientWidth={containerWidth}>
        {#if containerWidth}
        <Svg
            width={containerWidth}
            height={chartHeight}
            aria-labelledby="timeline-desc"
        >
            <text id="timeline-desc" x="-1000">A line chart showing the vote share each candidate had from the beginning of the election to the most recent update.</text>
            <g aria-hidden="true" class="chart-label" transform={`translate(15, ${chartHeight/2}) rotate(270)`} text-anchor="middle" >
                <text>Share of Vote</text>
            </g>
            <g aria-hidden="true" class="x-axis" transform={`translate(${chartOffset*2}, ${chartHeight - 10})`}>
                {#each xTicks as tick}
                <text x={xScale(tick)}>{dtFormat(tick).toLowerCase()}</text>
                {/each}
            </g>
            <g aria-hidden="true" class="y-axis" transform={`translate(${chartOffset}, 0)`}>
                {#each yTicks as tick}
                <text x={chartOffset - 5} y={yScale(tick) + 5} text-anchor="end">{tick}%</text>
                <line x1={chartOffset} x2={containerWidth - chartOffset} y1={yScale(tick)} y2={yScale(tick)}></line>
                {/each}
            </g>
            <g aria-hidden="true" class="line-graph" transform={`translate(${chartOffset*2},0)`}>
                {#each results as candidate}
                    <path stroke={candidate.color} d={lineGen(candidate.times)}></path>
                {/each}
            </g>
            <g class="mouseover-detect" transform={`translate(${chartOffset * 2}, 0)`}>
                <rect 
                    class="empty" 
                    width={containerWidth - chartOffset * 2}
                    tabindex="0"
                    y={15}
                    height={chartHeight - yPadding}
                    on:mouseover={handleMouseover}
                    on:mousemove={handleMousemove}
                    on:mouseleave={handleMouseleave}
                    on:focus={handleMouseover}
                    on:blur={handleMouseleave}
                    on:keydown={handleKeydown}
                    aria-describedby="timeline-tooltip"
                ></rect>
                {#if lineMouse.hovered && lineMouse.x}
                <line class="hover-line" x1={lineMouse.x} x2={lineMouse.x} y1={15} y2={chartHeight-yPadding}></line>
                {/if}
            </g>
        </Svg>
        {/if}
    </div>
    <div class="timeline-tip">
        {#if containerWidth && screenWidth}
        <Tooltip
            x={$tooltip.x > screenWidth / 2 ? $tooltip.x - 250 : $tooltip.x}
            y={$tooltip.y}
            visible={$tooltip.visible}
            isMobile={containerWidth < 500}
            id="timeline-tooltip"
        >
            {#if Object.keys($tooltip.data).length > 0}
            <div class="tip-head">{$tooltip.data.time}</div>
            <div class="tip-content">
                {#if $tooltip.data.timeResults.length > 0}
                <table>
                    <thead>
                        <tr>
                            <th class="tip-cell left">Candidate</th>
                            <th class="tip-cell right">% Vote</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each $tooltip.data.timeResults.sort(sortTooltip) as candidate}
                        <tr>
                            <td class="tip-cell left">
                                <span class="tip-square" style={`background-color: ${candidate.color}`}></span>
                                {candidate.last_name}
                            </td>
                            <td class="tip-cell right">{Math.round(candidate.vote_share * 10) / 10}</td>
                        </tr>
                        {/each}
                    </tbody>
                </table>
                {:else}
                <div class="tip-no-data">No Results Yet</div>
                {/if}
            </div>
            {/if}
        </Tooltip>
        {/if}
    </div>
</template>

<style>
    text {
        fill:  #42585C;
    }

    .y-axis {
        & line {
            stroke: #cdcdcd;
        }

        & text {
            font-size: .85rem;
        }
    }

    .x-axis {
        & text {
            font-size: .85rem;
            text-anchor: middle;
        }
    }

    .line-graph {
        & path {
            fill: none;
            stroke-width: 2;
        }
    }
    rect.empty {
        fill: none;
        stroke: none;
        pointer-events: all;
    }
    line.hover-line {
        stroke: red;
        stroke-dasharray: 4 2 4;
    }

    .tip-head {
        border-bottom: 1px solid #ddd;
        font-size: 1rem;
        text-align: center;
    }

    .tip-content {
        font-size: .85rem;
        & table {
            margin-left: 2em;
        }
    }

    .tip-cell {
        padding-right: 4.5em;
        white-space: nowrap;

        &.right {
            text-align: right;
        }

        &.left {
            text-align: left;
        }
    }

    .tip-square {
        width: .8rem;
        height: .8rem;
        border-radius: 50%;
        display: inline-block;
    }

    rect.empty:focus {
        stroke: $orange;
    }

    .tip-no-data {
        font-style: italic;
        text-align: center;
    }

    path, line, text {
        pointer-events: none;
    }

    :global(#timeline-tooltip) {
        pointer-events: none;
    }

    @media only screen and (max-width: 450px) {
        .x-axis text, .y-axis text {
            font-size: .75rem;
        }
    }
</style>